import moment from "moment";

const DATE_FORMAT_STR = 'h A z';

export interface DateRange {
  startAt: Date;
  endAt: Date;
};

export type GraphTimePeriod = 'last_month' | 'this_month' | 'last_week' | 'this_week' | 'yesterday' | 'today';

export type GraphGranularity = 'MINUTES' | 'HOURS' | 'DAYS' | 'MONTHS';

/**
 * Returns a text description of a time period - suitable for display.
 * @param period 
 * @returns 
 */
export const describeTimePeriod = (period: GraphTimePeriod) => {
  switch (period) {
    case 'last_month':
      return 'Last Month';
    case 'this_month':
      return 'This Month';
    case 'last_week':
      return 'Last Week';
    case 'this_week':
      return 'This Week';
    case 'yesterday':
      return 'Yesterday';
    case 'today':
    default:
      return 'Today';
  };
}

/**
 * Determines the granularity to render graph data in given the time period.
 * (E.g when viewing a month, show data by day, when viewing a day, show data by hour)
 * 
 * @param period 
 * @returns 
 */
export const granularityForPeriod = (period: GraphTimePeriod): GraphGranularity => {
  switch (period) {
    case 'last_month':
    case 'this_month':
    case 'last_week':
    case 'this_week':
      return 'DAYS';
    case 'yesterday':
    case 'today':
    default:
      return 'HOURS';
  };
}

/**
 * Move back or forward in time to the next time period.
 * nextPeriod('last_month', 1) => 'this_month'
 * nextPeriod('today', -1) => 'yesterday'
 * 
 * @param currentPeriod 
 * @param direction 
 * @returns 
 */
export const nextPeriod = (currentPeriod: GraphTimePeriod, direction: number): GraphTimePeriod => {
  const periods: GraphTimePeriod[] = ['last_month', 'this_month', 'last_week', 'this_week', 'yesterday', 'today'];

  const currentIndex = periods.indexOf(currentPeriod);
  const nextIndex = (currentIndex + direction);

  if (nextIndex < 0) {
    return periods[0];
  }

  if (nextIndex >= periods.length) {
    return periods[periods.length - 1];
  }

  return periods[nextIndex];
};

/**
 * Renders the text for a tick on the x-axis of the mileage graph. 
 * The tick label depends on the time period being viewed.
 * 
 * @param time 
 * @param period 
 * @returns 
 */
export const tickFormatter = (time: Date, period: GraphTimePeriod) => {
  const timeMoment = moment(time);

  switch(period) {
    case 'last_month':
    case 'this_month':
      return timeMoment.format('Do');
    case 'last_week':
    case 'this_week':
      return timeMoment.format('ddd');
    case 'yesterday':
    case 'today':
    default:
      return timeMoment.format(DATE_FORMAT_STR);
  }
};

/**
 * Renders the text for a tooltip on the mileage graph.
 * The tooltip depends on the time period being viewed.
 * 
 * @param time 
 * @param period 
 * @returns 
 */
export const tooltipFormatter = (time: Date, period: GraphTimePeriod) => {
  const timeMoment = moment(time);
  
  switch(period) {
    case 'last_month':
    case 'this_month':
    case 'last_week':
    case 'this_week':
      return timeMoment.format('ddd Do of MMMM');
    case 'yesterday':
    case 'today':
    default:
      return timeMoment.format(DATE_FORMAT_STR);
  }
}
